import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '../api/client';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

type RoastBatchLot = {
  lot_id: number;
  lot_code: string;
  quantity_used: number;
  bag_weight?: number;
  remaining_weight?: number;
  lot_product_id?: number;
  lot_product_name?: string;
};

type RoastBatch = {
  id: number;
  batch_code?: string | null;
  product_id: number;
  product_name: string;
  roast_date: string;
  green_weight: number;
  roasted_weight: number;
  agtron_whole?: number | null;
  agtron_ground?: number | null;
  profile_name?: string;
  operator?: string;
  moisture_loss?: number;
  notes?: string;
  lots?: RoastBatchLot[];
};

type RoastLog = {
  id: number;
  object_key: string;
  content_type?: string;
  size: number;
  created_at: string;
};

type RoastPlanBatch = {
  id: number;
  plan_id: number;
  index_num: number;
  roasted_weight: number;
  green_weight: number;
  roast_date?: string | null;
  status: string;
  lots: Array<{ id: number; lot_id: number; planned_green_weight: number }>;
};

type RoastPlan = {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  sales_order_id?: number | null;
  lot_id?: number | null;
  shrinkage_percent: number;
  total_roasted_weight: number;
  batch_roasted_weight: number;
  total_green_weight_planned: number;
  status: string;
  roast_date?: string | null;
  notes?: string | null;
  batches: RoastPlanBatch[];
};

type RoastBatchForm = {
  product_id: number;
  roast_date: string;
  green_weight: number;
  roasted_weight: number;
  agtron_whole?: number;
  agtron_ground?: number;
  profile_name?: string;
  operator?: string;
  moisture_loss?: number;
  notes?: string;
};

type RoastBatchPayload = RoastBatchForm & {
  lots: Array<{ lot_id: number; quantity: number }>;
};

type GreenLot = {
  id: number;
  lot_code: string;
  product_id: number;
  product_name: string;
  product_sku: string;
  bag_weight: number;
  remaining_weight: number;
  status: string;
  supplier_name?: string | null;
};

type LotAllocation = {
  lot_id: number;
  lot_code: string;
  remaining_weight: number;
  quantity: number;
};

const RoastBatchesPage = () => {
  const queryClient = useQueryClient();
  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<Array<{ id: number; name: string; is_green_coffee?: number }>>('/api/products')
  });

  const lotsQuery = useQuery({
    queryKey: ['green-lots', { includeDepleted: false }],
    queryFn: () => api.get<GreenLot[]>('/api/green-lots')
  });

  const batchesQuery = useQuery({
    queryKey: ['roast-batches'],
    queryFn: () => api.get<RoastBatch[]>('/api/roast-batches')
  });


  const roastPlansQuery = useQuery({
    queryKey: ['roast-plans', { status: 'draft' }],
    queryFn: () => api.get<RoastPlan[]>('/api/roast-plans?status=draft')
  });
  const [filter, setFilter] = useState('');
  const [historySortKey, setHistorySortKey] = useState<'batch' | 'date' | 'product' | 'green' | 'roasted'>('date');
  const [historySortDir, setHistorySortDir] = useState<'asc' | 'desc'>('desc');

  const form = useForm<RoastBatchForm>({
    defaultValues: {
      roast_date: new Date().toISOString().slice(0, 10),
      green_weight: 0,
      roasted_weight: 4.25
    }
  });

  const roastPlanForm = useForm<{
    product_id: number;
    lot_id: number;
    total_roasted_weight: number;
    batch_roasted_weight: number;
    shrinkage_percent?: number;
    roast_date?: string;
    notes?: string;
  }>({
    defaultValues: {
      total_roasted_weight: 10,
      batch_roasted_weight: 5,
      shrinkage_percent: 15,
      roast_date: new Date().toISOString().slice(0, 10)
    }
  });

  const [planError, setPlanError] = useState<string | null>(null);

  const nonGreenProducts = useMemo(
    () => (productsQuery.data ?? []).filter((p) => (p.is_green_coffee ?? 0) === 0),
    [productsQuery.data]
  );

  const [allocations, setAllocations] = useState<LotAllocation[]>([]);
  const [selectedLotId, setSelectedLotId] = useState<number | ''>('');
  const [lotQuantity, setLotQuantity] = useState<number>(0);
  const [lotValidation, setLotValidation] = useState<string | null>(null);

  const availableLots = useMemo(
    () => (lotsQuery.data ?? []).filter((lot) => lot.status !== 'depleted'),
    [lotsQuery.data]
  );

  const totalAllocated = useMemo(
    () => allocations.reduce((sum, lot) => sum + lot.quantity, 0),
    [allocations]
  );

  useEffect(() => {
    const roundedTotal = Number(totalAllocated.toFixed(6));
    const current = form.getValues('green_weight') ?? 0;
    if (Math.abs(current - roundedTotal) > 1e-6) {
      form.setValue('green_weight', roundedTotal, { shouldValidate: true });
    }
  }, [form, totalAllocated]);

  const createBatch = useMutation({
    mutationFn: (values: RoastBatchPayload) => api.post<RoastBatch>('/api/roast-batches', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roast-batches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['green-lots'] });
      setAllocations([]);
      setSelectedLotId('');
      setLotQuantity(0);
      setLotValidation(null);
      form.reset({
        roast_date: new Date().toISOString().slice(0, 10),
        green_weight: 0,
        roasted_weight: 4.25,
        agtron_whole: undefined,
        agtron_ground: undefined
      });
    }
  });

  const uploadLog = useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const buffer = await file.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);
      return api.post<RoastLog>(`/api/roast-batches/${id}/logs`, {
        filename: file.name,
        content_type: file.type,
        base64
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roast-batches'] });
      queryClient.invalidateQueries({ queryKey: ['roast-logs', variables.id] });
    }
  });

  const createRoastPlan = useMutation({
    mutationFn: (values: {
      product_id: number;
      lot_id: number;
      total_roasted_weight: number;
      batch_roasted_weight: number;
      shrinkage_percent?: number;
      roast_date?: string;
      notes?: string;
    }) => api.post('/api/roast-plans', {
      product_id: Number(values.product_id),
      lot_id: Number(values.lot_id),
      total_roasted_weight: Number(values.total_roasted_weight),
      batch_roasted_weight: Number(values.batch_roasted_weight),
      shrinkage_percent:
        values.shrinkage_percent !== undefined && !Number.isNaN(values.shrinkage_percent)
          ? Number(values.shrinkage_percent)
          : undefined,
      roast_date: values.roast_date,
      notes: values.notes && values.notes.trim().length > 0 ? values.notes.trim() : undefined
    }),
    onSuccess: () => {
      setPlanError(null);
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
      roastPlanForm.reset({
        total_roasted_weight: 10,
        batch_roasted_weight: 5,
        shrinkage_percent: 15,
        roast_date: new Date().toISOString().slice(0, 10),
        notes: ''
      });
    },
    onError: async (err: unknown) => {
      try {
        const e = err as Error;
        const parsed = JSON.parse(e.message) as { error?: string; details?: unknown };
        setPlanError(parsed.error ?? 'Failed to create roast plan.');
      } catch {
        setPlanError('Failed to create roast plan.');
      }
    }
  });

  const [planConfirmError, setPlanConfirmError] = useState<string | null>(null);
  const confirmRoastPlan = useMutation({
    mutationFn: (id: number) => api.post(`/api/roast-plans/${id}/confirm`, {}),
    onSuccess: () => {
      setPlanConfirmError(null);
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
      queryClient.invalidateQueries({ queryKey: ['roast-batches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['green-lots'] });
    },
    onError: (err: unknown) => {
      try {
        const e = err as Error;
        const parsed = JSON.parse(e.message) as { error?: string };
        setPlanConfirmError(parsed.error ?? 'Failed to confirm plan.');
      } catch {
        setPlanConfirmError('Failed to confirm plan.');
      }
    }
  });

  // Inline editing state for draft batches
  const [editedPlanBatches, setEditedPlanBatches] = useState<Record<number, { roasted_weight?: number; green_weight?: number; roast_date?: string }>>({});
  const [editedPlanLotWeights, setEditedPlanLotWeights] = useState<Record<number, number>>({});
  const [addLotByBatch, setAddLotByBatch] = useState<Record<number, { lot_id?: number; planned_green_weight?: number }>>({});

  const [pendingUpdateBatchId, setPendingUpdateBatchId] = useState<number | null>(null);
  const [batchErrors, setBatchErrors] = useState<Record<number, string | null>>({});
  const updatePlanBatch = useMutation({
    mutationFn: ({ planId, batchId, data }: { planId: number; batchId: number; data: { roasted_weight?: number; green_weight?: number; roast_date?: string } }) =>
      api.put(`/api/roast-plans/${planId}/batches/${batchId}`, data),
    onMutate: (vars) => {
      setPendingUpdateBatchId(vars.batchId);
    },
    onSuccess: () => {
      setBatchErrors((prev) => ({ ...prev, [pendingUpdateBatchId ?? -1]: null }));
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
    },
    onError: (err: unknown) => {
      try {
        const e = err as Error;
        const parsed = JSON.parse(e.message) as { error?: string };
        setBatchErrors((prev) => ({ ...prev, [pendingUpdateBatchId ?? -1]: parsed.error ?? 'Failed to update batch.' }));
      } catch {
        setBatchErrors((prev) => ({ ...prev, [pendingUpdateBatchId ?? -1]: 'Failed to update batch.' }));
      }
    },
    onSettled: () => {
      setPendingUpdateBatchId(null);
    }
  });

  const [pendingConfirmBatchId, setPendingConfirmBatchId] = useState<number | null>(null);
  const confirmPlanBatch = useMutation({
    mutationFn: ({ planId, batchId }: { planId: number; batchId: number }) =>
      api.post(`/api/roast-plans/${planId}/batches/${batchId}/confirm`, {}),
    onMutate: (vars) => {
      setPendingConfirmBatchId(vars.batchId);
    },
    onSuccess: () => {
      setBatchErrors((prev) => ({ ...prev, [pendingConfirmBatchId ?? -1]: null }));
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
      queryClient.invalidateQueries({ queryKey: ['roast-batches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      queryClient.invalidateQueries({ queryKey: ['green-lots'] });
    },
    onError: (err: unknown) => {
      try {
        const e = err as Error;
        const parsed = JSON.parse(e.message) as { error?: string };
        setBatchErrors((prev) => ({ ...prev, [pendingConfirmBatchId ?? -1]: parsed.error ?? 'Failed to confirm batch.' }));
      } catch {
        setBatchErrors((prev) => ({ ...prev, [pendingConfirmBatchId ?? -1]: 'Failed to confirm batch.' }));
      }
    },
    onSettled: () => {
      setPendingConfirmBatchId(null);
    }
  });

  const [pendingPlanLotUpdateId, setPendingPlanLotUpdateId] = useState<number | null>(null);
  const [pendingPlanLotDeleteId, setPendingPlanLotDeleteId] = useState<number | null>(null);

  const addPlanBatchLot = useMutation({
    mutationFn: ({ planId, batchId, lot_id, planned_green_weight }: { planId: number; batchId: number; lot_id: number; planned_green_weight: number }) =>
      api.post(`/api/roast-plans/${planId}/batches/${batchId}/lots`, { lot_id, planned_green_weight }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
    },
    onError: (err: unknown) => {
      console.error(err);
    }
  });

  const updatePlanBatchLot = useMutation({
    mutationFn: ({ planId, batchId, planLotId, planned_green_weight }: { planId: number; batchId: number; planLotId: number; planned_green_weight: number }) =>
      api.put(`/api/roast-plans/${planId}/batches/${batchId}/lots/${planLotId}`, { planned_green_weight }),
    onMutate: (vars) => setPendingPlanLotUpdateId(vars.planLotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
    },
    onSettled: () => setPendingPlanLotUpdateId(null)
  });

  const deletePlanBatchLot = useMutation({
    mutationFn: ({ planId, batchId, planLotId }: { planId: number; batchId: number; planLotId: number }) =>
      api.del(`/api/roast-plans/${planId}/batches/${batchId}/lots/${planLotId}`),
    onMutate: (vars) => setPendingPlanLotDeleteId(vars.planLotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roast-plans'] });
    },
    onSettled: () => setPendingPlanLotDeleteId(null)
  });

  const [activeTab, setActiveTab] = useState<'history' | 'log' | 'plans'>('history');
  const [showDraftPlans, setShowDraftPlans] = useState(true);

  // Plan preview modal
  const [previewPlanId, setPreviewPlanId] = useState<number | null>(null);
  const [isPlanPreviewOpen, setIsPlanPreviewOpen] = useState(false);
  const previewPlanQuery = useQuery({
    queryKey: previewPlanId ? ['roast-plans', previewPlanId] : ['roast-plans', 'none'],
    queryFn: () => api.get<RoastPlan>(`/api/roast-plans/${previewPlanId}`),
    enabled: !!previewPlanId
  });

  const handleAddLot = () => {
    if (!selectedLotId || lotQuantity <= 0) {
      setLotValidation('Select a lot and quantity greater than zero.');
      return;
    }

    const lot = availableLots.find((item) => item.id === selectedLotId);
    if (!lot) {
      setLotValidation('The selected lot is no longer available.');
      return;
    }

    if (lotQuantity > lot.remaining_weight + 1e-6) {
      setLotValidation('Requested quantity exceeds the remaining weight for this lot.');
      return;
    }

    setAllocations((prev) => {
      const existing = prev.find((item) => item.lot_id === lot.id);
      if (!existing) {
        return [
          ...prev,
          {
            lot_id: lot.id,
            lot_code: lot.lot_code,
            remaining_weight: lot.remaining_weight,
            quantity: Number(lotQuantity.toFixed(6))
          }
        ];
      }
      const updatedQuantity = existing.quantity + lotQuantity;
      if (updatedQuantity > lot.remaining_weight + 1e-6) {
        setLotValidation('Total allocated quantity cannot exceed the lot remaining weight.');
        return prev;
      }
      return prev.map((item) =>
        item.lot_id === lot.id ? { ...item, quantity: Number(updatedQuantity.toFixed(6)) } : item
      );
    });

    setLotValidation(null);
    setLotQuantity(0);
    setSelectedLotId('');
  };

  const handleAllocationChange = (lotId: number, quantity: number) => {
    const lotSource = availableLots.find((lot) => lot.id === lotId) ?? allocations.find((lot) => lot.lot_id === lotId);
    if (!lotSource) {
      return;
    }
    if (quantity < 0) {
      quantity = 0;
    }
    if (quantity > lotSource.remaining_weight + 1e-6) {
      setLotValidation('Allocated quantity cannot exceed the lot remaining weight.');
      return;
    }
    setAllocations((prev) =>
      prev.map((item) => (item.lot_id === lotId ? { ...item, quantity: Number(quantity.toFixed(6)) } : item))
    );
    setLotValidation(null);
  };

  const handleRemoveLot = (lotId: number) => {
    setAllocations((prev) => prev.filter((item) => item.lot_id !== lotId));
  };

  const onSubmit = (values: RoastBatchForm) => {
    if (allocations.length === 0) {
      setLotValidation('Add at least one green coffee lot.');
      form.setError('green_weight', { type: 'manual', message: 'Add at least one green coffee lot.' });
      return;
    }

    const totalLots = allocations.reduce((sum, lot) => sum + lot.quantity, 0);
    const roundedTotalLots = Number(totalLots.toFixed(6));
    if (Math.abs(roundedTotalLots - values.green_weight) > 0.01) {
      setLotValidation('Allocated lot quantity must match the total green weight.');
      form.setError('green_weight', {
        type: 'manual',
        message: 'Allocated lot quantity must match the total green weight.'
      });
      return;
    }

    form.clearErrors('green_weight');
    setLotValidation(null);

    const toOptional = (input?: string) => (input && input.trim().length > 0 ? input.trim() : undefined);
    const toOptionalNumber = (input?: number) =>
      input !== undefined && !Number.isNaN(input) ? Number(input) : undefined;

    const payload: RoastBatchPayload = {
      product_id: Number(values.product_id),
      roast_date: values.roast_date,
      green_weight: roundedTotalLots,
      roasted_weight: Number(values.roasted_weight),
      agtron_whole: toOptionalNumber(values.agtron_whole),
      agtron_ground: toOptionalNumber(values.agtron_ground),
      profile_name: toOptional(values.profile_name),
      operator: toOptional(values.operator),
      moisture_loss:
        values.moisture_loss !== undefined && !Number.isNaN(values.moisture_loss)
          ? Number(values.moisture_loss)
          : undefined,
      notes: toOptional(values.notes),
      lots: allocations.map((lot) => ({ lot_id: lot.lot_id, quantity: Number(lot.quantity.toFixed(6)) }))
    };

    createBatch.mutate(payload);
  };

  return (
    <div>
      <h2>Roast Batches</h2>
      <p style={{ color: '#64748b' }}>
        Record production runs, tie them to green coffee lots, and archive roast logs to R2.
        Each roast adjusts inventory for green usage and roasted output with full traceability.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'history', label: 'Production History', badge: (batchesQuery.data ?? []).length },
          { id: 'log', label: 'Log Roast' },
          { id: 'plans', label: 'Roast Planning' }
        ]}
      />

      {activeTab === 'plans' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Plan a roast</h3>
          <form
          className="form-grid"
          onSubmit={roastPlanForm.handleSubmit((values) => createRoastPlan.mutate(values))}
        >
          <div className="form-group">
            <label>Product</label>
            <select {...roastPlanForm.register('product_id', { required: true, valueAsNumber: true })}>
              <option value="">Select product</option>
              {nonGreenProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Allocate green lot</label>
            <select {...roastPlanForm.register('lot_id', { required: true, valueAsNumber: true })}>
              <option value="">Select lot</option>
              {availableLots.map((lot) => (
                <option key={lot.id} value={lot.id}>
                  {lot.lot_code} ({lot.remaining_weight.toFixed(2)} kg left)
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Total roasted weight (kg)</label>
            <input
              type="number"
              step="0.01"
              {...roastPlanForm.register('total_roasted_weight', { valueAsNumber: true, required: true })}
            />
          </div>
          <div className="form-group">
            <label>Batch roasted weight (kg)</label>
            <input
              type="number"
              step="0.01"
              {...roastPlanForm.register('batch_roasted_weight', { valueAsNumber: true, required: true })}
            />
          </div>
          <div className="form-group">
            <label>Shrinkage %</label>
            <input
              type="number"
              step="0.1"
              {...roastPlanForm.register('shrinkage_percent', { valueAsNumber: true })}
            />
          </div>
          <div className="form-group">
            <label>Target roast date</label>
            <input type="date" {...roastPlanForm.register('roast_date')} />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Notes</label>
            <textarea rows={2} {...roastPlanForm.register('notes')} />
          </div>
            <button className="btn primary" type="submit" disabled={createRoastPlan.isPending}>
              {createRoastPlan.isPending ? 'Planning…' : 'Create roast plan'}
            </button>
            {planError && (
              <p style={{ color: '#dc2626', fontSize: 12, gridColumn: 'span 2' }}>{planError}</p>
            )}
          </form>
        </div>
      ) : null}

      {activeTab === 'plans' ? (
        <section style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Draft roast plans</h3>
            <button className="btn" type="button" onClick={() => setShowDraftPlans((v) => !v)}>
              {showDraftPlans ? 'Hide' : 'Show'}
            </button>
          </div>
          {showDraftPlans && (
          <>
          {roastPlansQuery.isLoading && <p style={{ color: '#64748b' }}>Loading plans…</p>}
          {!roastPlansQuery.isLoading && roastPlansQuery.data && roastPlansQuery.data.length === 0 && (
            <p style={{ color: '#64748b' }}>No draft roast plans.</p>
          )}
          {roastPlansQuery.data && roastPlansQuery.data.length > 0 && (
            <div className="table-wrapper" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product</th>
                  <th>Total roasted</th>
                  <th>Batch size</th>
                  <th>Shrinkage %</th>
                  <th>Total green planned</th>
                  <th>Batches</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {roastPlansQuery.data.map((plan) => (
                  <tr key={plan.id}>
                    <td>{plan.id}</td>
                    <td>
                      {plan.product_name}
                      <span style={{ color: '#94a3b8', fontSize: 12 }}> ({plan.product_sku})</span>
                    </td>
                    <td>{plan.total_roasted_weight.toFixed(2)} kg</td>
                    <td>{plan.batch_roasted_weight.toFixed(2)} kg</td>
                    <td>{plan.shrinkage_percent.toFixed(1)}%</td>
                    <td>{plan.total_green_weight_planned.toFixed(2)} kg</td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {plan.batches.map((b) => {
                          const edited = editedPlanBatches[b.id] || {};
                          return (
                            <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span className="badge">#{b.index_num}</span>
                              <label style={{ fontSize: 12, color: '#475569' }}>Roasted</label>
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={b.roasted_weight}
                                onChange={(e) =>
                                  setEditedPlanBatches((prev) => ({
                                    ...prev,
                                    [b.id]: { ...prev[b.id], roasted_weight: Number(e.target.value) }
                                  }))
                                }
                                style={{ width: 110 }}
                              />
                              <label style={{ fontSize: 12, color: '#475569' }}>Green</label>
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={b.green_weight}
                                onChange={(e) =>
                                  setEditedPlanBatches((prev) => ({
                                    ...prev,
                                    [b.id]: { ...prev[b.id], green_weight: Number(e.target.value) }
                                  }))
                                }
                                style={{ width: 110 }}
                              />
                              <input
                                type="date"
                                defaultValue={(b.roast_date ?? plan.roast_date ?? new Date().toISOString().slice(0, 10)).slice(0, 10)}
                                onChange={(e) =>
                                  setEditedPlanBatches((prev) => ({
                                    ...prev,
                                    [b.id]: { ...prev[b.id], roast_date: e.target.value }
                                  }))
                                }
                              />
                              <button
                                className="btn secondary"
                                type="button"
                                onClick={() =>
                                  updatePlanBatch.mutate({
                                    planId: plan.id,
                                    batchId: b.id,
                                    data: {
                                      roasted_weight: edited.roasted_weight ?? b.roasted_weight,
                                      green_weight: edited.green_weight ?? b.green_weight,
                                      roast_date: edited.roast_date ?? b.roast_date ?? plan.roast_date ?? new Date().toISOString().slice(0, 10)
                                    }
                                  })
                                }
                                disabled={pendingUpdateBatchId === b.id}
                              >
                                {pendingUpdateBatchId === b.id ? 'Saving…' : 'Update'}
                              </button>
                              <button
                                className="btn primary"
                                type="button"
                                onClick={() => confirmPlanBatch.mutate({ planId: plan.id, batchId: b.id })}
                                disabled={pendingConfirmBatchId === b.id}
                              >
                                {pendingConfirmBatchId === b.id ? 'Confirming…' : 'Confirm batch'}
                              </button>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <strong>Lots</strong>
                                  <span style={{ color: '#64748b', fontSize: 12 }}>
                                    Total planned: {
                                      b.lots.reduce((sum, l) => sum + l.planned_green_weight, 0).toFixed(2)
                                    } kg • Batch green: {b.green_weight.toFixed(2)} kg
                                  </span>
                                  {Math.abs(
                                    b.lots.reduce((sum, l) => sum + l.planned_green_weight, 0) - b.green_weight
                                  ) > 0.01 && (
                                    <button
                                      className="btn secondary"
                                      type="button"
                                      onClick={() =>
                                        updatePlanBatch.mutate({
                                          planId: plan.id,
                                          batchId: b.id,
                                          data: { green_weight: b.green_weight }
                                        })
                                      }
                                      disabled={pendingUpdateBatchId === b.id}
                                    >
                                      {pendingUpdateBatchId === b.id ? 'Rebalancing…' : 'Rebalance to batch'}
                                    </button>
                                  )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {b.lots.map((pl) => {
                                    const lotInfo = availableLots.find((al) => al.id === pl.lot_id);
                                    const lotLabel = lotInfo ? `${lotInfo.lot_code} (${lotInfo.remaining_weight.toFixed(2)} kg left)` : `Lot #${pl.lot_id}`;
                                    const currentVal = editedPlanLotWeights[pl.id] ?? pl.planned_green_weight;
                                    return (
                                      <div key={pl.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span className="badge">{lotLabel}</span>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={currentVal}
                                          onChange={(e) =>
                                            setEditedPlanLotWeights((prev) => ({ ...prev, [pl.id]: Number(e.target.value) }))
                                          }
                                          style={{ width: 120 }}
                                        />
                                        <button
                                          className="btn secondary"
                                          type="button"
                                          onClick={() =>
                                            updatePlanBatchLot.mutate({
                                              planId: plan.id,
                                              batchId: b.id,
                                              planLotId: pl.id,
                                              planned_green_weight: Number(currentVal)
                                            })
                                          }
                                          disabled={pendingPlanLotUpdateId === pl.id}
                                        >
                                          {pendingPlanLotUpdateId === pl.id ? 'Saving…' : 'Save lot'}
                                        </button>
                                        <button
                                          className="btn"
                                          type="button"
                                          onClick={() => deletePlanBatchLot.mutate({ planId: plan.id, batchId: b.id, planLotId: pl.id })}
                                          disabled={pendingPlanLotDeleteId === pl.id}
                                        >
                                          {pendingPlanLotDeleteId === pl.id ? 'Removing…' : 'Remove lot'}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>

                                <AddPlanLotInline
                                  planId={plan.id}
                                  batchId={b.id}
                                  availableLots={availableLots}
                                  onAdd={(args) => addPlanBatchLot.mutate(args)}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn primary"
                        onClick={async () => {
                          setPlanConfirmError(null);
                          try {
                            await confirmRoastPlan.mutateAsync(plan.id);
                          } catch (e) {
                            // error is handled in onError
                          }
                        }}
                        disabled={confirmRoastPlan.isPending}
                      >
                        {confirmRoastPlan.isPending ? 'Confirming…' : 'Confirm plan'}
                      </button>
                      <button
                        className="btn"
                        type="button"
                        onClick={() => {
                          setPreviewPlanId(plan.id);
                          setIsPlanPreviewOpen(true);
                        }}
                      >
                        Preview plan
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {planConfirmError && (
          <p style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{planConfirmError}</p>
        )}
        </>
        )}
      </section>
      ) : null}

      {isPlanPreviewOpen && previewPlanId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            zIndex: 1000
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="card" style={{ width: 'min(960px, 95vw)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0 }}>Roast plan preview</h3>
                <p style={{ color: '#64748b', margin: '4px 0 0' }}>
                  {previewPlanQuery.data?.product_name}
                  <span style={{ color: '#94a3b8' }}> ({previewPlanQuery.data?.product_sku})</span>
                </p>
              </div>
              <button className="btn" onClick={() => setIsPlanPreviewOpen(false)}>Close</button>
            </div>

            {previewPlanQuery.isLoading && <p style={{ color: '#64748b' }}>Loading…</p>}
            {previewPlanQuery.data && (
              <div>
                <p style={{ color: '#475569' }}>
                  Total roasted: {previewPlanQuery.data.total_roasted_weight.toFixed(2)} kg • Batch size: {previewPlanQuery.data.batch_roasted_weight.toFixed(2)} kg • Total green planned: {previewPlanQuery.data.total_green_weight_planned.toFixed(2)} kg
                </p>
                <div className="table-wrapper" style={{ marginTop: 8 }}>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Roasted (kg)</th>
                        <th>Green (kg)</th>
                        <th>Lots</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewPlanQuery.data.batches.map((b) => (
                        <tr key={b.id}>
                          <td>#{b.index_num}</td>
                          <td>{b.roasted_weight.toFixed(2)}</td>
                          <td>{b.green_weight.toFixed(2)}</td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {b.lots.map((pl) => {
                                const lot = availableLots.find((al) => al.id === pl.lot_id);
                                const label = lot ? `${lot.lot_code}` : `Lot #${pl.lot_id}`;
                                return (
                                  <span key={pl.id} className="badge">
                                    {label} — {pl.planned_green_weight.toFixed(2)} kg
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                  <button
                    className="btn primary"
                    disabled={confirmRoastPlan.isPending}
                    onClick={async () => {
                      if (!previewPlanId) return;
                      try {
                        await confirmRoastPlan.mutateAsync(previewPlanId);
                        setIsPlanPreviewOpen(false);
                      } catch (e) {}
                    }}
                  >
                    {confirmRoastPlan.isPending ? 'Confirming…' : 'Confirm plan'}
                  </button>
                  <button className="btn" onClick={() => setIsPlanPreviewOpen(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === 'log' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Log a roast</h3>
          <form className="form-grid" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="form-group">
            <label>Product</label>
            <select {...form.register('product_id', { valueAsNumber: true, required: true })}>
              <option value="">Select product</option>
              {productsQuery.data?.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Roast date</label>
            <input type="date" {...form.register('roast_date')} />
          </div>
          <div className="form-group">
            <label>Green weight (kg)</label>
            <input
              type="number"
              step="0.01"
              readOnly
              {...form.register('green_weight', { valueAsNumber: true })}
            />
            <p style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
              Calculated from allocated lot quantities.
            </p>
            {form.formState.errors.green_weight && (
              <p style={{ color: '#dc2626', fontSize: 12 }}>{form.formState.errors.green_weight.message}</p>
            )}
          </div>
          <div className="form-group">
            <label>Roasted weight (kg)</label>
            <input type="number" step="0.01" {...form.register('roasted_weight', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Agtron (whole bean)</label>
            <input
              type="number"
              step="0.1"
              {...form.register('agtron_whole', { valueAsNumber: true })}
            />
          </div>
          <div className="form-group">
            <label>Agtron (ground)</label>
            <input
              type="number"
              step="0.1"
              {...form.register('agtron_ground', { valueAsNumber: true })}
            />
          </div>
          <div className="form-group">
            <label>Profile name</label>
            <input {...form.register('profile_name')} />
          </div>
          <div className="form-group">
            <label>Roaster</label>
            <input {...form.register('operator')} />
          </div>
          <div className="form-group">
            <label>Moisture loss %</label>
            <input type="number" step="0.01" {...form.register('moisture_loss', { valueAsNumber: true })} />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Allocate green coffee lots</label>
            <div
              style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <select
                value={selectedLotId}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedLotId(value ? Number(value) : '');
                }}
              >
                <option value="">Select lot</option>
                {availableLots.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.lot_code} ({lot.remaining_weight.toFixed(2)} kg left)
                  </option>
                ))}
              </select>
              <input
                type="number"
                step="0.01"
                min={0}
                placeholder="Quantity kg"
                value={lotQuantity || ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setLotQuantity(value === '' ? 0 : Number(value));
                }}
                style={{ width: 140 }}
              />
              <button type="button" className="btn" onClick={handleAddLot}>
                Add lot
              </button>
            </div>
            <p
              style={{
                color: '#475569',
                fontSize: 12,
                marginTop: 8
              }}
            >
              Allocated {totalAllocated.toFixed(2)} kg • Green weight auto-fills from this total.
            </p>
            {lotValidation && (
              <p style={{ color: '#dc2626', fontSize: 12 }}>{lotValidation}</p>
            )}
            {allocations.length > 0 && (
              <div className="table-wrapper" style={{ marginTop: 12 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Lot</th>
                      <th>Remaining</th>
                      <th>Allocated</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.map((lot) => (
                      <tr key={lot.lot_id}>
                        <td>{lot.lot_code}</td>
                        <td>{lot.remaining_weight.toFixed(2)} kg</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            max={lot.remaining_weight}
                            value={lot.quantity}
                            onChange={(event) => handleAllocationChange(lot.lot_id, Number(event.target.value))}
                            style={{ width: 120 }}
                          />
                        </td>
                        <td>
                          <button type="button" className="btn" onClick={() => handleRemoveLot(lot.lot_id)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Notes</label>
            <textarea rows={2} {...form.register('notes')} />
          </div>
            <button className="btn primary" type="submit" disabled={createBatch.isPending}>
              {createBatch.isPending ? 'Logging...' : 'Create roast batch'}
            </button>
          </form>
        </div>
      ) : null}

      {activeTab === 'history' ? (
        <section style={{ marginTop: 16 }}>
          <h3>Production history</h3>
          <TableToolbar value={filter} onChange={setFilter} placeholder="Filter production batches…" />
          <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th
                  onClick={() => {
                    if (historySortKey === 'batch') setHistorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    setHistorySortKey('batch');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Batch{historySortKey === 'batch' ? (historySortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (historySortKey === 'date') setHistorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    setHistorySortKey('date');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Date{historySortKey === 'date' ? (historySortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (historySortKey === 'product') setHistorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    setHistorySortKey('product');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Product{historySortKey === 'product' ? (historySortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (historySortKey === 'green') setHistorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    setHistorySortKey('green');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Green{historySortKey === 'green' ? (historySortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (historySortKey === 'roasted') setHistorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    setHistorySortKey('roasted');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Roasted{historySortKey === 'roasted' ? (historySortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th>Agtron WB/G</th>
                <th>Lots</th>
                <th>Logs</th>
                <th>Upload</th>
              </tr>
            </thead>
            <tbody>
              {(batchesQuery.data ?? [])
                .filter((row) => {
                  if (!filter) return true;
                  const f = filter.toLowerCase();
                  const hay = [
                    row.batch_code ?? `#${row.id}`,
                    row.roast_date,
                    row.product_name,
                    String(row.green_weight),
                    String(row.roasted_weight)
                  ]
                    .join(' ')
                    .toLowerCase();
                  return hay.includes(f);
                })
                .sort((a, b) => {
                  const dir = historySortDir === 'asc' ? 1 : -1;
                  if (historySortKey === 'batch') {
                    const va = a.batch_code ?? `#${a.id}`;
                    const vb = b.batch_code ?? `#${b.id}`;
                    return va.localeCompare(vb) * dir;
                  }
                  if (historySortKey === 'date') return (new Date(a.roast_date).getTime() - new Date(b.roast_date).getTime()) * dir;
                  if (historySortKey === 'product') return a.product_name.localeCompare(b.product_name) * dir;
                  if (historySortKey === 'green') return (a.green_weight - b.green_weight) * dir;
                  return (a.roasted_weight - b.roasted_weight) * dir;
                })
                .map((batch) => (
                <tr key={batch.id}>
                  <td>{batch.batch_code ?? `#${batch.id}`}</td>
                  <td>{new Date(batch.roast_date).toLocaleDateString()}</td>
                  <td>{batch.product_name}</td>
                  <td>{batch.green_weight.toFixed(2)} kg</td>
                  <td>{batch.roasted_weight.toFixed(2)} kg</td>
                  <td>
                    {batch.agtron_whole != null || batch.agtron_ground != null ? (
                      <span>
                        {batch.agtron_whole != null ? batch.agtron_whole.toFixed(1) : '—'} /{' '}
                        {batch.agtron_ground != null ? batch.agtron_ground.toFixed(1) : '—'}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {batch.lots && batch.lots.length > 0 ? (
                        batch.lots.map((lot) => (
                          <span key={`${batch.id}-${lot.lot_id}`} className="badge">
                            {lot.lot_code} ({lot.quantity_used.toFixed(2)} kg)
                          </span>
                        ))
                      ) : (
                        <span className="badge">No lot link</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <RoastLogs batchId={batch.id} />
                  </td>
                  <td>
                    <input
                      type="file"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                          uploadLog.mutate({ id: batch.id, file });
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      ) : null}
    </div>
  );
};

const AddPlanLotInline = ({
  planId,
  batchId,
  availableLots,
  onAdd
}: {
  planId: number;
  batchId: number;
  availableLots: GreenLot[];
  onAdd: (args: { planId: number; batchId: number; lot_id: number; planned_green_weight: number }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState<number | ''>('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSelectedLot('');
    setQuantity('');
    setError(null);
  };

  const handleAdd = () => {
    if (!selectedLot || !quantity || Number(quantity) <= 0) {
      setError('Select a lot and enter a quantity greater than zero.');
      return;
    }
    const lot = availableLots.find((l) => l.id === selectedLot);
    if (!lot) {
      setError('Selected lot is not available.');
      return;
    }
    if (Number(quantity) > lot.remaining_weight + 1e-6) {
      setError('Quantity exceeds remaining weight for this lot.');
      return;
    }
    onAdd({ planId, batchId, lot_id: Number(selectedLot), planned_green_weight: Number(quantity) });
    reset();
    setOpen(false);
  };

  if (!open) {
    return (
      <div>
        <button className="btn" type="button" onClick={() => setOpen(true)}>
          Add component
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select
        value={selectedLot}
        onChange={(e) => setSelectedLot(e.target.value ? Number(e.target.value) : '')}
      >
        <option value="">Select lot to add</option>
        {availableLots.map((lot) => (
          <option key={lot.id} value={lot.id}>
            {lot.lot_code} ({lot.remaining_weight.toFixed(2)} kg left)
          </option>
        ))}
      </select>
      <input
        type="number"
        step="0.01"
        placeholder="Qty kg"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
        style={{ width: 120 }}
      />
      <button className="btn secondary" type="button" onClick={handleAdd}>
        Add
      </button>
      <button
        className="btn"
        type="button"
        onClick={() => {
          reset();
          setOpen(false);
        }}
      >
        Cancel
      </button>
      {error && <span style={{ color: '#dc2626', fontSize: 12 }}>{error}</span>}
    </div>
  );
};

const RoastLogs = ({ batchId }: { batchId: number }) => {
  const logsQuery = useQuery({
    queryKey: ['roast-logs', batchId],
    queryFn: () => api.get<RoastLog[]>(`/api/roast-batches/${batchId}/logs`)
  });

  if (!logsQuery.data || logsQuery.data.length === 0) {
    return <span className="badge">No logs</span>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {logsQuery.data.map((log) => (
        <span key={log.id} className="badge">
          {log.object_key.split('/').pop()} ({Math.round(log.size / 1024)} kb)
        </span>
      ))}
    </div>
  );
};

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

export default RoastBatchesPage;
